import { useState, useEffect } from 'react';
import { fetchData } from '../hooks/useDataRefresh';

const BADGE_COLORS = {
  'SALESv2':              { bg: 'var(--info-soft)',    border: 'var(--info-border)',    text: 'var(--info)' },
  'SCANv2':               { bg: 'var(--success-soft)', border: 'var(--success-border)', text: 'var(--success)' },
  'SCAN TOTAL GROCERY':   { bg: 'var(--success-soft)', border: 'var(--success)',        text: 'var(--success)' },
  'NielsenIQ':            { bg: 'var(--ai-soft)',      border: 'var(--ai-border)',      text: 'var(--ai-text)' },
  'Promo Grid (Excel)':   { bg: 'var(--warn-soft)',    border: 'var(--warn-border)',    text: 'var(--warn-text)' },
  'FORECAST.xlsx':        { bg: '#ffedd5',             border: '#ea580c',               text: '#c2410c' }, // intentional orange — distinct from amber warn
  'Manual / localStorage':{ bg: 'var(--bg-input)',     border: 'var(--text-muted)',     text: 'var(--text-body)' },
  'Computed':             { bg: '#cffafe',             border: '#0891b2',               text: '#0e7490' }, // intentional cyan — distinct from blue info
};

function Badge({ source }) {
  const s = BADGE_COLORS[source] || BADGE_COLORS['Manual / localStorage'];
  return (
    <span style={{
      display: 'inline-block',
      background: s.bg,
      border: `1px solid ${s.border}`,
      color: s.text,
      borderRadius: 4,
      padding: '2px 8px',
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {source}
    </span>
  );
}

// Live file status component — fetches the file and extracts metadata
function FileStatus({ filename, label }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetchData(filename)
      .then(d => {
        const meta = typeof d === 'object' && !Array.isArray(d) ? (d.metadata || d) : {};
        const skus = d?.skus?.length ?? (Array.isArray(d) ? d.length : null);
        setStatus({
          builtAt: meta.built_at || null,
          lastWeek: meta.last_actual_week || null,
          count: skus,
          ok: true,
        });
      })
      .catch(() => setStatus({ ok: false }));
  }, [filename]);

  if (!status) return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>loading…</span>;
  if (!status.ok) return <span style={{ color: 'var(--danger)', fontSize: 11 }}>not found</span>;

  const parts = [];
  if (status.builtAt) {
    const d = new Date(status.builtAt);
    parts.push(d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }));
  }
  if (status.lastWeek) parts.push(`last actual W${status.lastWeek}`);
  if (status.count != null) parts.push(`${status.count} records`);

  return (
    <span style={{ fontSize: 11 }}>
      <span style={{ color: 'var(--success)', marginRight: 4 }}>&#10003;</span>
      <span style={{ color: 'var(--text-muted)' }}>{parts.join(' · ') || 'loaded'}</span>
    </span>
  );
}

// Each entry: { section, source, dataset, file, notes }
const TAB_DATA = [
  {
    tab: 'Business Overview',
    icon: '📊',
    rows: [
      {
        section: 'Customer Revenue table (TY / LY / Budget / Forecast)',
        source: 'SALESv2',
        dataset: 'PLANAUDIT workspace',
        file: 'salesv2-customers.json',
        notes: 'GSV, NSV, CM by customer. Built by dashboard/scripts → salesv2_q2_customer_ty_ly_budget_forecast',
      },
      {
        section: 'Period P&L chart (monthly GSV vs budget)',
        source: 'SALESv2',
        dataset: 'PLANAUDIT workspace',
        file: 'salesv2-periods.json',
        notes: 'P1–P13 GSV TY vs LY vs budget vs forecast',
      },
      {
        section: 'Forward Orders pipeline',
        source: 'SALESv2',
        dataset: 'PLANAUDIT workspace',
        file: 'salesv2-forward-orders.json',
        notes: 'Open confirmed orders by customer. Built from salesv2_q5_forward_orders_pipeline',
      },
      {
        section: 'Opportunity Pipeline',
        source: 'SALESv2',
        dataset: 'PLANAUDIT workspace',
        file: 'salesv2-opportunities.json',
        notes: 'New business / ranging opportunities by customer and probability. Built from salesv2_q8_opportunity_pipeline',
      },
      {
        section: 'Brand YTD scan callouts (Bhuja / Infuzions RSV)',
        source: 'SCANv2',
        dataset: 'DEMAND workspace',
        file: 'scanv2-brand.json',
        notes: 'Brand-level YTD RSV TY vs LY for WW and Coles. Built by build_market_data.py',
      },
      {
        section: 'Household Penetration panel (NielsenIQ)',
        source: 'NielsenIQ',
        dataset: 'Omnishopper — Total Outlets Omni',
        file: 'niq-penetration.json',
        notes: 'HH penetration % and buyer count TY vs LY. Manually updated from NielsenIQ Omnishopper report',
      },
    ],
  },
  {
    tab: 'Market Trends',
    icon: '📈',
    rows: [
      {
        section: 'Category market share — Nuts & Snacks subcategory RSV/units',
        source: 'SCAN TOTAL GROCERY',
        dataset: 'DEMAND workspace',
        file: 'scantg-market.json',
        notes: 'Market RSV by subcategory (Snack Nuts, Snack Mix, etc.) for WW and Coles. 13W and 52W views. Built by build_market_data.py',
      },
      {
        section: 'Majans brand weekly scan trend (weekly RSV chart)',
        source: 'SCANv2',
        dataset: 'DEMAND workspace',
        file: 'scanv2-weekly.json',
        notes: 'Weekly RSV/units for Bhuja and Infuzions vs category. Built by build_market_data.py',
      },
      {
        section: 'Brand YTD performance KPIs',
        source: 'SCANv2',
        dataset: 'DEMAND workspace',
        file: 'scanv2-brand.json',
        notes: 'YTD RSV by brand and retailer',
      },
      {
        section: 'Household Penetration panel',
        source: 'NielsenIQ',
        dataset: 'Omnishopper — Total Outlets Omni',
        file: 'niq-penetration.json',
        notes: 'HH penetration % and buyer count TY vs LY. Manually updated from NielsenIQ Omnishopper report',
      },
    ],
  },
  {
    tab: 'Scan Sales',
    icon: '🛒',
    rows: [
      {
        section: 'Weekly RSV / units trend chart',
        source: 'SCANv2',
        dataset: 'DEMAND workspace',
        file: 'scanv2-weekly.json',
        notes: 'Week-by-week RSV and units for Bhuja and Infuzions vs category total. Built by build_market_data.py',
      },
      {
        section: 'YTD summary KPIs (RSV, units, ranged stores, selling stores)',
        source: 'SCANv2',
        dataset: 'DEMAND workspace',
        file: 'scanv2-ytd.json',
        notes: 'Aggregated FY2026 YTD figures. Built by build_market_data.py',
      },
      {
        section: 'Brand YTD split (Bhuja / Infuzions by retailer)',
        source: 'SCANv2',
        dataset: 'DEMAND workspace',
        file: 'scanv2-brand.json',
        notes: 'YTD RSV TY vs LY by brand family and retailer',
      },
    ],
  },
  {
    tab: 'Forecast',
    icon: '🔮',
    rows: [
      {
        section: 'Actual scan sales — WW & Coles (TY RSV / units)',
        source: 'SCANv2',
        dataset: 'DEMAND workspace — SCAN table',
        file: 'scanv2-sku-weekly.json',
        notes: 'Per-SKU weekly ty_rsv and ty_units for WW and Coles from FY2026. Built by build_sku_weekly.py using SCAN[SALES], SCAN[UNITS], SCAN[AVG_PRICE], SCAN[NDISTSOLD]',
      },
      {
        section: 'Actual sell-in — ALDI, Costco & Metcash (past Mfg Forecast weeks)',
        source: 'FORECAST.xlsx',
        dataset: 'FORECAST.xlsx sell-in model',
        file: 'scanv2-sku-weekly.json (mfg_fcst_units)',
        notes: 'No scan data available for these retailers. Past weeks of the M-Forecast (mfg_fcst_units) represent actual manufacturer sell-in and are shown on the Actual line. Injected by build_mfg_forecast.py',
      },
      {
        section: 'Last year reference line (LY RSV / units)',
        source: 'SCANv2',
        dataset: 'DEMAND workspace — SCAN table',
        file: 'scanv2-sku-weekly.json',
        notes: 'Per-SKU weekly ly_rsv and ly_units (FY2025 same week). Stored alongside TY in same file. WW and Coles only',
      },
      {
        section: 'Average selling price (Avg Price line)',
        source: 'SCANv2',
        dataset: 'DEMAND workspace — SCAN table',
        file: 'scanv2-sku-weekly.json',
        notes: 'Weighted average price from SCAN[AVG_PRICE]. Only displayed at single-SKU level. WW and Coles only',
      },
      {
        section: 'Customer Forecast — Woolworths (Cust Forecast line)',
        source: 'SCANv2',
        dataset: 'DEMAND workspace — WWFORECAST table',
        file: 'scanv2-sku-weekly.json',
        notes: 'WWFORECAST[BASELINEUNITS] — WW replenishment demand forecast in consumer units per SKU per week. Stored as cust_fcst_units. RSV estimated via last known avg price',
      },
      {
        section: 'Customer Forecast — Coles (Cust Forecast line)',
        source: 'SCANv2',
        dataset: 'DEMAND workspace — COLESFORECAST table',
        file: 'scanv2-sku-weekly.json',
        notes: 'COLESFORECAST[TOTALUNITS] summed across DCs — Coles DC replenishment forecast in consumer units per SKU per week',
      },
      {
        section: 'Mfg Forecast line — all retailers',
        source: 'FORECAST.xlsx',
        dataset: 'FORECAST.xlsx sell-in model',
        file: 'scanv2-sku-weekly.json (mfg_fcst_units)',
        notes: 'Majans sell-in forecast from FORECAST.xlsx converted to consumer units (carton qty × consumer units/carton). Injected by build_mfg_forecast.py. For WW/Coles: future weeks only. For ALDI/Costco/Metcash: future weeks shown here; past weeks shown on Actual line',
      },
      {
        section: 'Promo tooltips (price, tactic)',
        source: 'Promo Grid (Excel)',
        dataset: 'Master Promotional Grid All Accounts.xlsx',
        file: 'src/data/promo-calendar.js',
        notes: 'Auto-generated JS module from the Master Promo Grid Excel. Contains WW and Coles promo prices and tactics by SKU assortment and FY2026 week number. Suppressed for non-scan retailers',
      },
      {
        section: 'Last actual week (dynamic Actual | Forecast boundary)',
        source: 'Computed',
        dataset: 'Derived from scanv2-sku-weekly.json metadata',
        file: 'scanv2-sku-weekly.json',
        notes: 'Read from metadata.last_actual_week written by build_sku_weekly.py. Marks the boundary between confirmed scan actuals and forecast territory on the chart',
      },
    ],
  },
  {
    tab: 'Distribution',
    icon: '📦',
    rows: [
      {
        section: 'Distribution by SKU (ranged %, selling %, ROS)',
        source: 'SCANv2',
        dataset: 'DEMAND workspace',
        file: 'scanv2-distribution.json',
        notes: 'Per-SKU distribution metrics for WW and Coles — % stores ranged, % stores selling. Built by build_market_data.py',
      },
      {
        section: 'YTD summary callouts',
        source: 'SCANv2',
        dataset: 'DEMAND workspace',
        file: 'scanv2-ytd.json',
        notes: 'Total ranged and selling store counts YTD',
      },
    ],
  },
  {
    tab: 'Store Performance',
    icon: '🏪',
    rows: [
      {
        section: 'Brand-level store performance KPIs',
        source: 'SCANv2',
        dataset: 'DEMAND workspace',
        file: 'scanv2-brand.json',
        notes: 'YTD RSV by brand and retailer used to populate store-level context',
      },
      {
        section: 'Store distribution breakdown',
        source: 'SCANv2',
        dataset: 'DEMAND workspace',
        file: 'scanv2-distribution.json',
        notes: 'Per-SKU selling store counts and ranged store counts by retailer',
      },
    ],
  },
  {
    tab: 'Promotions',
    icon: '🏷️',
    rows: [
      {
        section: 'Full promo calendar (all weeks, all assortments)',
        source: 'Promo Grid (Excel)',
        dataset: 'Master Promotional Grid All Accounts.xlsx',
        file: 'src/data/promo-calendar.js',
        notes: 'Auto-generated from the Master Promo Grid Excel file. Contains WW and Coles promo events, prices, tactics and mechanic types by assortment and FY2026 week',
      },
      {
        section: 'Key dates overlay (Diwali, Easter, etc.)',
        source: 'Promo Grid (Excel)',
        dataset: 'Master Promotional Grid All Accounts.xlsx',
        file: 'src/data/promo-calendar.js',
        notes: 'MAJANS_KEY_DATES constant embedded in promo-calendar.js',
      },
    ],
  },
  {
    tab: 'Promo Effectiveness',
    icon: '📉',
    rows: [
      {
        section: 'Promo event ROI / efficiency metrics',
        source: 'commercial.PromoPerformance (SQL)',
        dataset: 'Typed table — edited in-app',
        file: 'api: /api/promo-performance',
        notes: 'Promo event records with retailer, brand, mechanic, activation type, RSV uplift and ROI. Now a typed, governed, temporal table (migration 035) — add/edit events on the Promo Effectiveness page. Future: pull from SCANv2 and PLANAUDIT',
      },
    ],
  },
  {
    tab: 'Insights & Recommendations',
    icon: '💡',
    rows: [
      {
        section: 'All recommendation cards (text, priority, status)',
        source: 'Manual / database',
        dataset: 'commercial.Recommendations (Azure SQL)',
        file: 'commercial.Recommendations + RecommendationStatus (typed tables)',
        notes: 'Authored in-app, saved to commercial.Recommendations with a per-user status overlay (typed table; syncs across devices/users). Migration 002.',
      },
    ],
  },
  {
    tab: 'Key Dates',
    icon: '📅',
    rows: [
      {
        section: 'Customer sell-in milestones (Engagement / Decision / DC / POG / Range Review)',
        source: 'dbo.KeySellDates (MAGIC)',
        dataset: 'Shared magic DB — owned by app-magic',
        file: 'api: /api/key-dates (read-only)',
        notes: 'Read-only mirror of MAGIC Key Sell Dates. Source of truth + all editing live in MAGIC (magic.majans.com/sell-dates); app-commercial reads dbo.KeySellDates directly.',
      },
    ],
  },
  {
    tab: 'Commercials',
    icon: '💰',
    rows: [
      {
        section: 'All pricing and trading terms inputs (RRP, List Price, OI Disc, DTT 1/2/3, Marginal Cost)',
        source: 'Manual / database',
        dataset: 'commercial.TradeTerm / TradeSpend (Azure SQL)',
        file: 'commercial.TradeTerm + commercial.TradeSpend (typed, shared, audited)',
        notes: 'Entered per customer per assortment, saved to the typed shared trade-terms/spend tables (FK to commercial.Customer; writes audited). Future: pull Marginal Cost from PLANAUDIT standard cost',
      },
      {
        section: 'Invoice Price, Net Sell Price, Retailer Margin %, Our Margin %',
        source: 'Computed',
        dataset: 'Derived from manual inputs',
        file: '—',
        notes: 'Calculated in-browser: Invoice = List × (1−OI%), Net Sell = Invoice × (1−DTT1%−DTT2%) − DTT3$, Retailer Margin = (RRP÷1.1 − Net Sell) ÷ RRP÷1.1, Our Margin = (Net Sell − Cost) ÷ Net Sell',
      },
    ],
  },
];

// Source system legend
const SYSTEMS = [
  { source: 'SALESv2',              desc: 'Majans internal P&L — PLANAUDIT workspace (Power BI via connector-fabric). DAX queries via XMLA endpoint' },
  { source: 'SCANv2',               desc: 'Majans scan sales by SKU — DEMAND workspace (Power BI via connector-fabric). Includes WWFORECAST and COLESFORECAST tables' },
  { source: 'SCAN TOTAL GROCERY',   desc: 'GFK / market scan data — DEMAND workspace (Power BI via connector-fabric). Used for category-level market share and trends' },
  { source: 'NielsenIQ',            desc: 'NielsenIQ Omnishopper household penetration panel. Manually exported and uploaded to niq-penetration.json' },
  { source: 'Promo Grid (Excel)',    desc: 'Master Promotional Grid All Accounts.xlsx — maintained by commercial team. Converted to promo-calendar.js via script' },
  { source: 'FORECAST.xlsx',        desc: 'Majans sell-in forecast model (FORECAST.xlsx). Carton-level forecasts converted to consumer units by build_mfg_forecast.py. Used for WW, Coles, ALDI, Costco and Metcash M-Forecast lines' },
  { source: 'Manual / database',     desc: 'Data entered manually in-app, saved to typed commercial.* tables in Azure SQL (shared or per-user; writes are audited)' },
  { source: 'Computed',             desc: 'Values derived in-browser from other data. No external source — calculated on the fly' },
];

// Files to show live status for (keyed by filename)
const LIVE_FILES = [
  'salesv2-customers.json',
  'salesv2-periods.json',
  'salesv2-forward-orders.json',
  'salesv2-opportunities.json',
  'scanv2-brand.json',
  'scanv2-weekly.json',
  'scanv2-ytd.json',
  'scanv2-sku-weekly.json',
  'scanv2-distribution.json',
  'scantg-market.json',
  'niq-penetration.json',
  'forecast-model.json',
];

export default function DataSources() {
  // Live file status map: { filename -> { ok, builtAt, lastWeek, count } }
  const [fileStatuses, setFileStatuses] = useState({});

  useEffect(() => {
    LIVE_FILES.forEach(filename => {
      fetchData(filename)
        .then(d => {
          const meta = typeof d === 'object' && !Array.isArray(d) ? (d.metadata || d) : {};
          const countKey = ['skus','customers','rows','weeks','items','periods'].find(k => Array.isArray(d?.[k]));
          const count = countKey ? d[countKey].length : Array.isArray(d) ? d.length : null;
          setFileStatuses(prev => ({
            ...prev,
            [filename]: {
              ok: true,
              builtAt: meta.built_at || null,
              lastWeek: meta.last_actual_week || null,
              count,
            },
          }));
        })
        .catch(() => {
          setFileStatuses(prev => ({ ...prev, [filename]: { ok: false } }));
        });
    });
  }, []);

  function StatusPill({ filename }) {
    const s = fileStatuses[filename];
    if (!s) return <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>loading…</span>;
    if (!s.ok) return <span style={{ color: '#ef4444', fontSize: 10 }}>not found</span>;
    const parts = [];
    if (s.builtAt) {
      const d = new Date(s.builtAt);
      parts.push(d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }));
    }
    if (s.lastWeek) parts.push(`last actual W${s.lastWeek}`);
    if (s.count != null) parts.push(`${s.count} records`);
    return (
      <span style={{ fontSize: 10, color: '#22c55e' }}>
        &#10003; {parts.join(' · ') || 'loaded'}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <div>
        <h2 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600 }}>Data Sources</h2>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          Every section of every tab — exactly where the data comes from. File status updates on refresh.
        </div>
      </div>

      {/* Source system legend */}
      <div className="card">
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-body)', marginBottom: 12 }}>Source Systems</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {SYSTEMS.map(s => (
            <div key={s.source} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flexShrink: 0, paddingTop: 1 }}><Badge source={s.source} /></div>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{s.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Live file status panel */}
      <div className="card">
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-body)', marginBottom: 12 }}>Live File Status</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
          {LIVE_FILES.map(f => (
            <div key={f} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: 'var(--bg-elevated)', borderRadius: 6, border: '1px solid var(--border)' }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{f}</span>
              <StatusPill filename={f} />
            </div>
          ))}
        </div>
      </div>

      {/* Per-tab sections */}
      {TAB_DATA.map(tab => (
        <div key={tab.tab} className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Tab header */}
          <div style={{
            background: 'var(--bg-header)',
            padding: '12px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{ fontSize: 14 }}>{tab.icon}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{tab.tab}</span>
          </div>

          {/* Rows table */}
          <table>
            <thead>
              <tr>
                <th style={{ width: '26%' }}>Section / Data element</th>
                <th style={{ width: '13%' }}>Source</th>
                <th style={{ width: '18%' }}>Dataset / System</th>
                <th style={{ width: '18%' }}>File</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {tab.rows.map((row, i) => {
                // Find a matching file status if the file column is a known filename
                const knownFile = LIVE_FILES.find(f => row.file === f || row.file.startsWith(f));
                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 500, verticalAlign: 'top', padding: '10px 16px' }}>
                      {row.section}
                    </td>
                    <td style={{ verticalAlign: 'top', padding: '10px 12px' }}>
                      <Badge source={row.source} />
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text-muted)', verticalAlign: 'top', padding: '10px 12px' }}>
                      {row.dataset}
                    </td>
                    <td style={{ fontSize: 11, verticalAlign: 'top', padding: '10px 12px', fontFamily: 'monospace' }}>
                      <div style={{ color: 'var(--text-muted)' }}>{row.file}</div>
                      {knownFile && (
                        <div style={{ marginTop: 3 }}>
                          <StatusPill filename={knownFile} />
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text-muted)', verticalAlign: 'top', padding: '10px 12px', lineHeight: 1.6 }}>
                      {row.notes}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
